import React, { Suspense } from 'react';
import { SingleFadeLoader } from 'components/SingleFadeLoader';
import {
  Dashboard,
  DonutSmall,
  DriveEta,
  FireTruck,
  Handyman,
  Hub,
  Insights,
  LocalGasStation,
  LocalShipping,
  NotificationsActive,
  PrecisionManufacturing,
  Route,
  RvHookup,
  Settings,
  Troubleshoot,
  Assessment,
  CloudUpload,
} from '@mui/icons-material';
import AnalyticsIcon from '@mui/icons-material/Analytics';
import TireRepairIcon from '@mui/icons-material/TireRepair';
import AddRoadIcon from '@mui/icons-material/AddRoad';
import EditLocationAltIcon from '@mui/icons-material/EditLocationAlt';
import { DTCView } from 'views/EngineHours/DTCView';
import ControlRoom from 'views/ControlRoom';
import { UsageDash } from 'views/FuelAnalytics/UsageDashboard';
import FuelUsageOverview from 'views/FuelUsage';
import MlInsights from 'views/Insights';
import NetworkHealth from 'views/NetworkHealth/NetworkHealth';
import Production from 'views/Production';
import { NotificationsView } from 'views/Settings/Notifications/NotificationsView';
import Utilization from 'views/Utilization';
import VehicleStatus from 'views/VehicleStatus';
import DashboardView from 'views/TirePressure/Dashboard';
import FleetInsight from 'views/TirePressure/FleetInsight';
import VehicleInsight from 'views/TirePressure/VehicleInsight';
import { DashbaordEntityManagementViewV2 } from 'views/Settings/ExternalUsers/Squares/DashboardV2/DashboardEntityManagementViewV2';
import { CostAnalysisView } from 'views/Settings/ExternalUsers/Squares/CostAnalysis/CostAnalysisView';
// import { MaintenanceEntityManagementView } from 'views/Settings/ExternalUsers/Squares/Maintenance/MaintenanceEntityManagementView';
import { WorkOrdersView } from 'views/Settings/ExternalUsers/Squares/WorkOrders/WorkOrdersView';
import { OverviewView } from 'views/SurvivalAnalysis/Overview/OverviewView';
import MlInsightDetails from 'views/Insights/Details';
import { InternalUsersView } from 'views/Settings/InternalUsers/InternalUsersView';
import { EntityManagementView } from 'views/Settings/ExternalUsers/EntityManagementView';
import { CustomersView } from 'views/Settings/Customer/CustomerView';
import { EditCustomerContactView } from 'views/Settings/ExternalUsers/EditCustomerContactView/EditCustomerContactView';
import { NotificationView } from 'views/Settings/Rules/NotificationView';
import { TagView } from 'views/Settings/Tag/TagView';
import { DriverBehaviorSettingsView } from 'views/Settings/DriverBehavior/DriverBehaviorSettingsView';
import Productivity from 'views/Reports/Productivity';
import FleetComparisonNew from 'views/FleetComparison/FleetComparisonNew';
import SensorAnalysisNew from 'views/SensorAnalysisView/SensorAnalysisNew';
import { TpmSSensorAssignment } from 'views/TirePressure/SensorAssignment';
import { WorkOrderManagement } from 'views/Settings/ExternalUsers/Squares/WorkOrderManagement/WorkOrderManagementView';
import PlanningAid from 'views/PlanningAid';
import { CompareAssetsWrapper } from './CompareAssetsWrapper';
import { SquaresDashboardV3 } from 'views/Settings/ExternalUsers/Squares/DashboardV3';
import VirtualLoadTest from 'views/VirtualLoadTest';
import TireInventory from 'views/TireManagement/TireInventory';
import TireInspection from 'views/TireManagement/TireInspection';
import Timeline from 'views/TireManagement/Timeline';
import TimelineDetail from 'views/TireManagement/Timeline/TimelineDetail';
import { TireIcon } from 'components/Icons/TireIcon';
import { DispatchSummary } from 'views/FleetPlanner/DispatchSummary/DispatchSummary';
import { BlendMonitoring } from 'views/BlendMonitoring/BlendMonitoring';
import { LocationImporterView } from 'views/DispatchImporters/LocationImporterView';
import { GradeImporterView } from 'views/DispatchImporters/GradeImporterView';
import { BoundaryImporterView } from 'views/DispatchImporters/BoundaryImporterView';
import { OperatorImporterView } from 'views/DispatchImporters/OperatorImporterView';
import { RoadsImporterView } from 'views/DispatchImporters/RoadsImporterView';
import { CreateDispatchPlan } from 'views/FleetPlanner/CreateDispatchPlan/CreateDispatchPlan';
import { SiteConfigV2View } from 'views/FleetPlanner/SiteConfigV2/SiteConfigV2View';
import DispatchPlanV2View from 'views/FleetPlanner/DispatchPlanV2/DispatchPlanV2View';
import HaulersMonitorView from 'views/FleetPlanner/HaulersMonitor/HaulersMonitorView';
import AssignmentBoardView from 'views/FleetPlanner/AssignmentBoard/AssignmentBoardView';
import AssetsStatusView from 'views/FleetPlanner/AssetsStatus/AssetsStatusView';
import { type INavigationConfigStaticItem } from 'views/Settings/NavigationMap/types/types';
import { AlertsView } from 'views/Settings/ExternalUsers/Squares/Alerts';
import ScheduleReport from 'views/ScheduleReport/index';
import CriticalAssets from 'views/FrameFatigue/CriticalAssets';
import DispatchMonitor from 'views/DispatchMonitor';
import { StatusCodesView } from 'views/Settings/StatusCodes/StatusCodesView';
import { DevicesTabletsStatusView } from 'views/Settings/DevicesTabletsStatus/DevicesTabletsStatusView';
import { NotificationPreferencesView } from 'views/Settings/NotificationPreferences/NotificationPreferencesView';

const FleetOverview = React.lazy(
  async () =>
    await import('views/FleetOverview/FleetOverview').then((m) => ({
      default: m.FleetOverview,
    }))
);

const DriverBehavior = React.lazy(
  async () => await import('views/DriverBehavior/DriverBehavior')
);

const InclineRoadCondition = React.lazy(
  async () =>
    await import('views/DriverBehavior/Inclinometer').then((m) => ({
      default: m.InclineRoadCondition,
    }))
);

const EngineHoursTabView = React.lazy(
  async () => await import('views/EngineHours/EngineHoursTabView')
);

const ProductionOld = React.lazy(
  async () => await import('views/FuelAnalytics/Production/Production')
);

const RoadConditionSummary = React.lazy(
  async () => await import('views/RoadConditionSummary')
);

const NewRoadCondition = React.lazy(
  async () => await import('views/NewRoadCondition')
);

const LeakageAnalysis = React.lazy(
  async () => await import('views/TPMS Leakage Analysis/LeakageAnalysis')
);

const TPMSLive = React.lazy(
  async () =>
    await import('views/TPMSLive/TPMSLive').then((m) => ({
      default: m.TPMSLive,
    }))
);

const FrameFatigueOverview = React.lazy(
  async () => await import('views/FrameFatigue/FrameFatigueOverview')
);

export const staticNavigationMap: INavigationConfigStaticItem[] = [
  // Fleet Overview
  {
    id: 'fleetOverview',
    path: 'fleet',
    icon: <LocalShipping />,
    children: [
      {
        id: 'fleetSummary',
        path: 'summary',
        component: (
          <Suspense fallback={<SingleFadeLoader isFullScreen={false} />}>
            <FleetOverview />
          </Suspense>
        ),
      },
      {
        id: 'vehicleStatus',
        path: 'vehicle-status',
        // icon: <DirectionsCar />, -- optional
        component: <VehicleStatus />,
      },
      {
        id: 'compareAssets',
        path: 'compare-assets',
        // icon: <DirectionsCar />, -- optional
        component: <CompareAssetsWrapper />,
      },
    ],
  },
  {
    id: 'controlRoom',
    path: 'control-room',
    icon: <Assessment />,
    component: <ControlRoom />,
  },
  // Fleet Planner
  {
    id: 'fleetPlanner',
    path: 'fleet-planner',
    icon: <RvHookup />,
    children: [
      {
        id: 'dispatchSummary',
        path: 'dispatch-summary',
        component: <DispatchSummary />,
      },
      {
        id: 'createDispatchPlan',
        path: 'create-dispatch-plan',
        component: <CreateDispatchPlan />,
      },
      {
        id: 'dispatchMonitor',
        path: 'dispatch-monitor',
        component: <DispatchMonitor />,
      },
      {
        id: 'planningAid',
        path: 'planning-aid',
        component: <PlanningAid />,
      },
      {
        id: 'blendMonitoring',
        path: 'blend-monitoring',
        component: <BlendMonitoring />,
      },
      {
        id: 'siteConfigV2',
        path: 'site-config-v2',
        component: <SiteConfigV2View />,
      },
      {
        id: 'dispatchPlanV2',
        path: 'dispatch-plan-v2',
        component: <DispatchPlanV2View />,
      },
      {
        id: 'haulersMonitor',
        path: 'haulers-monitor',
        component: <HaulersMonitorView />,
      },
      {
        id: 'assignmentBoard',
        path: 'assignment-board',
        component: <AssignmentBoardView />,
      },
      {
        id: 'assetsStatus',
        path: 'assets-status',
        component: <AssetsStatusView />,
      },
    ],
  },
  // Dispatch Importers
  {
    id: 'dispatchImporters',
    path: 'dispatch-importers',
    icon: <CloudUpload />,
    children: [
      {
        id: 'locationImporter',
        path: 'location',
        component: <LocationImporterView />,
      },
      {
        id: 'gradeImporter',
        path: 'grade',
        component: <GradeImporterView />,
      },
      {
        id: 'boundaryImporter',
        path: 'boundary',
        component: <BoundaryImporterView />,
      },
      {
        id: 'operatorImporter',
        path: 'operator',
        component: <OperatorImporterView />,
      },
    ],
  },
  // Mine Map Studio — pulled out of Dispatch Importers into its own
  // top-level entry; it's a single standalone view, so it has no children.
  {
    id: 'roadsImporter',
    path: 'mine-map-studio',
    icon: <EditLocationAltIcon />,
    component: <RoadsImporterView />,
  },
  // Activity Tracker
  {
    id: 'engineHoursSummary',
    path: 'engine-hours-summary',
    icon: <Dashboard />,
    component: (
      <Suspense fallback={<SingleFadeLoader isFullScreen={false} />}>
        <EngineHoursTabView />
      </Suspense>
    ),
  },
  // Diagnostic Trouble Codes
  {
    id: 'dtcSummary',
    path: 'diagnostic-trouble-codes',
    icon: <Troubleshoot />,
    component: <DTCView />,
  },
  // Sensor Analysis
  {
    id: 'sensorAnalysisNew',
    path: 'sensor-analysis',
    icon: <FireTruck />,
    children: [
      {
        id: 'sensorAnalysisOverview',
        path: 'overview',
        component: <SensorAnalysisNew />,
      },
      {
        id: 'virtualLoadTest',
        path: 'virtual-load-test',
        component: <VirtualLoadTest />,
      },
    ],
  },
  // Notification Center
  {
    id: 'notifications',
    path: 'notifications',
    icon: <NotificationsActive />,
    component: <NotificationsView />,
  },
  // Insights
  {
    id: 'insights',
    path: 'ml-insights',
    icon: <Insights />,
    component: <MlInsights />,
  },
  // Insight Details
  {
    id: 'insightDetails',
    path: 'insights/details',
    icon: <></>,
    component: <MlInsightDetails />,
  },
  // Road Condition Summary
  {
    id: 'roadConditionSummary',
    path: 'road-condition-summary',
    icon: <Route />,
    component: (
      <Suspense fallback={<SingleFadeLoader isFullScreen={false} />}>
        <RoadConditionSummary />
      </Suspense>
    ),
  },
  // New Road Condition
  {
    id: 'newRoadCondition',
    path: 'new-road-condition',
    icon: <AddRoadIcon />,
    component: (
      <Suspense fallback={<SingleFadeLoader isFullScreen={false} />}>
        <NewRoadCondition />
      </Suspense>
    ),
  },
  // Frame Fatigue
  {
    id: 'frameFatigue',
    path: 'frame-fatigue',
    icon: <Insights />,
    children: [
      {
        id: 'frameFatigueOverview',
        path: 'overview',
        component: (
          <Suspense fallback={<SingleFadeLoader isFullScreen={false} />}>
            <FrameFatigueOverview />
          </Suspense>
        ),
      },
      {
        id: 'frameFatigueCriticalAssets',
        path: 'critical-assets',
        component: <CriticalAssets />,
      },
    ],
  },
  // Driver Behavior
  {
    id: 'driverBehavior',
    path: 'driver-behavior',
    icon: <DriveEta />,
    children: [
      {
        id: 'driverBehaviorOverview',
        path: 'overview',
        component: (
          <Suspense fallback={<SingleFadeLoader isFullScreen={false} />}>
            <DriverBehavior />
          </Suspense>
        ),
      },
      {
        id: 'terrainView',
        path: 'terrain-view',
        component: (
          <Suspense fallback={<SingleFadeLoader isFullScreen={false} />}>
            <InclineRoadCondition />
          </Suspense>
        ),
      },
    ],
  },
  // Network Health
  {
    id: 'networkHealth',
    path: 'network-health',
    icon: <Hub />,
    component: <NetworkHealth />,
  },
  // Fuel Usage
  {
    id: 'fuelUsage',
    path: 'fuelUsage',
    icon: <LocalGasStation />,
    children: [
      {
        id: 'fuelUsageOverview',
        path: 'overview',
        component: <FuelUsageOverview />,
      },
    ],
  },
  // Utilization
  {
    id: 'utilization',
    path: 'utilization',
    icon: <DonutSmall />,
    children: [
      {
        id: 'utilizationOverview',
        path: 'overview',
        component: <Utilization />,
      },
      {
        id: 'utilizationIdleAnalysis',
        path: 'idle-analysis',
        component: <UsageDash />,
      },
    ],
  },
  // Production
  {
    id: 'production',
    path: 'production',
    icon: <PrecisionManufacturing />,
    children: [
      {
        id: 'productionOverview',
        path: 'overview',
        component: <Production />,
      },
      {
        id: 'productionIdleAnalysis',
        path: 'load-cycle-analysis/:assetVin',
        component: (
          <Suspense fallback={<SingleFadeLoader isFullScreen={false} />}>
            <ProductionOld />
          </Suspense>
        ),
      },
    ],
  },
  // Tire Insights
  {
    id: 'tireInsights',
    path: 'tire-insights',
    icon: <TireIcon height={22} width={22} />,
    children: [
      {
        id: 'tireAnalyticsFleetInsight',
        path: 'fleet-insight',
        component: <FleetInsight />,
      },
      {
        id: 'tireAnalyticsVehicleInsight',
        path: 'vehicle-insight',
        component: <VehicleInsight />,
      },
      {
        id: 'tireAnalyticsTPMSLive',
        path: 'tpms-live',
        component: (
          <Suspense fallback={<SingleFadeLoader isFullScreen={false} />}>
            <TPMSLive />
          </Suspense>
        ),
      },
    ],
  },
  // Tire Analytics
  {
    id: 'tireAnalytics',
    path: 'tire-analytics',
    icon: <AnalyticsIcon />,
    children: [
      {
        id: 'tireAnalyticsOverview',
        path: 'notification',
        component: <DashboardView />,
      },
      {
        id: 'tireAnalyticsLeakageAnalysis',
        path: 'leakage-analysis',
        component: (
          <Suspense fallback={<SingleFadeLoader isFullScreen={false} />}>
            <LeakageAnalysis />
          </Suspense>
        ),
      },
    ],
  },
  // Tire Management
  {
    id: 'tireManagement',
    path: 'tire-management',
    icon: <TireRepairIcon />,
    children: [
      {
        id: 'tireInventory',
        path: 'tire-inventory',
        component: <TireInventory />,
      },
      {
        id: 'tireInspection',
        path: 'tire-inspection',
        component: <TireInspection />,
      },
      {
        id: 'tireTimeline',
        path: 'timeline',
        component: <Timeline />,
      },
    ],
  },
  // X Parts
  {
    id: 'xParts',
    path: 'x-parts',
    icon: <Handyman />,

    children: [
      {
        id: 'xPartsOverview',
        path: 'dashboard-old',

        component: <DashbaordEntityManagementViewV2 />,
      },
      {
        id: 'xPartsDashboard',
        path: 'dashboard',

        component: <SquaresDashboardV3 />,
      },
      {
        id: 'xPartsInsightsAndRecommendations',
        path: 'insights-and-recommendations',

        component: <CostAnalysisView />,
      },
      // {
      //   id: 'xPartsMaintenance',
      //   path: 'maintenance',
      //   component: <MaintenanceEntityManagementView />,
      // },
      {
        id: 'alerts',
        path: 'alerts',

        component: <AlertsView />,
      },
      {
        id: 'xPartsWorkOrders',
        path: 'work-orders',
        component: <WorkOrdersView />,
      },
      {
        id: 'xPartsSurvivalAnalysis',
        path: 'survival-analysis',
        component: <OverviewView />,
      },
      {
        id: 'xPartsUploadWorkOrders',
        path: 'upload-work-orders',
        component: <DashbaordEntityManagementViewV2 />,
      },
      {
        id: 'xPartsWorkOrderManagement',
        path: 'work-order-management',
        component: <WorkOrderManagement />,
      },
    ],
  },
  // Settings
  {
    id: 'settings',
    path: 'settings',
    icon: <Settings />,
    children: [
      {
        id: 'internalUsersSettings',
        path: 'symboticware-user',
        component: <InternalUsersView />,
      },
      {
        id: 'externalUsersSettings',
        path: 'user',
        component: <EntityManagementView />,
      },
      {
        id: 'customersSettings',
        path: 'customer-accounts',
        component: <CustomersView />,
      },
      {
        id: 'contactInformationSettings',
        path: 'contact-information',
        component: <EditCustomerContactView />,
      },
      {
        id: 'rulesSettings',
        path: 'rules',
        component: <NotificationView />,
      },
      {
        id: 'tagsSettings',
        path: 'tags',
        component: <TagView />,
      },
      {
        id: 'tpmsSensorsSettings',
        path: 'tpms-sensors',
        component: <TpmSSensorAssignment />,
      },
      {
        id: 'driverBehaviorSettings',
        path: 'driver-behavior',
        component: <DriverBehaviorSettingsView />,
      },
      {
        id: 'statusCodesSettings',
        path: 'status-codes',
        component: <StatusCodesView />,
      },
      {
        id: 'devicesTabletsStatusSettings',
        path: 'devices-tablets-status',
        component: <DevicesTabletsStatusView />,
      },
      {
        id: 'notificationPreferencesSettings',
        path: 'notification-preferences',
        component: <NotificationPreferencesView />,
      },
      // {
      //   id: 'naviationMap',
      //   path: 'navigation-map',
      //   component: <CustomerNavConfigurator />,
      // },
    ],
  },

  // reports
  {
    id: 'reports',
    path: 'reports',
    icon: <Assessment />,
    children: [
      {
        id: 'scheduleReport',
        path: 'schedule-report',
        component: <ScheduleReport />,
      },
    ],
  },
  // invisible routes
  {
    id: 'compareAssetsSideBarHidden',
    path: 'compare-assets',
    icon: <></>,
    component: <FleetComparisonNew />,
  },
  {
    id: 'reportsSideBarHidden',
    path: 'reports/productivity',
    component: <Productivity />,
    icon: <></>,
  },
  {
    id: 'tireTimelineDetail',
    path: 'tire-management/timeline/detail',
    component: <TimelineDetail />,
    icon: <></>,
  },
];
